/**
 * SiteEntry - TypeScript ES6 Module Migration
 * Global utility functions called by Blazor via IJSRuntime.
 * Migrated from jQuery-based site.js to typed ES6 module.
 *
 * NOTE: The legacy theme functions (Cornerstone, InsLight, InsDark) are preserved
 * for backward compat but should be replaced by ThemeManager.setTheme() calls.
 */

// Extend JQuery with jQuery UI sortable + Bootstrap modal (not in @types/jquery)
declare global {
    interface JQuery<TElement = HTMLElement> {
        sortable(options?: Record<string, unknown>): JQuery<TElement>;
        modal(action: string): JQuery<TElement>;
    }
}

// Hover-show behavior for legacy dropdown classes
function initHoverDropdowns(): void {
    if (typeof jQuery === 'undefined') return;
    const $ = jQuery;

    const classes: string[] = ['.Bugs', '.Theme', '.Settings', '.RowsperpageQuery', '.MathsQuery', '.FormatQuery', '.ReportQuery'];
    classes.forEach((cls: string) => {
        $(cls).hover(
            function (this: HTMLElement) { $(this).addClass('show'); },
            function (this: HTMLElement) { $(this).removeClass('show'); }
        );
    });
}

// Handle #focus scrolling aligned with bottom of navbar
function initHashScrolling(): void {
    if (typeof jQuery === 'undefined' || !window.location.hash) return;
    const $ = jQuery;

    window.scrollTo(0, 0);
    requestAnimationFrame(() => {
        const heading: JQuery = $("h5:contains('Site Information')");
        if (heading.length) {
            const navbar: JQuery = $('.navbar');
            const navbarHeight: number = navbar.outerHeight() || 0;
            const headingOffset = heading.offset();
            if (headingOffset) {
                window.scrollTo({ top: headingOffset.top - navbarHeight, behavior: 'smooth' });
            }
        }
    });
}

// Auto-initialize jQuery-dependent code when DOM ready
document.addEventListener('DOMContentLoaded', () => {
    initHoverDropdowns();
    initHashScrolling();
});

// ---- Blazor IJSRuntime exports ----

export function Sortable(id: string, isDisabled: boolean, dotNetObject: { invokeMethodAsync: (method: string, ...args: unknown[]) => Promise<void> }): void {
    if (typeof jQuery === 'undefined') return;
    const $ = jQuery;
    const $list = $('#' + id);
    $list.sortable({
        axis: 'y',
        disabled: isDisabled,
        stop: function () {
            const children: JQuery = $list.children();
            for (let i = 0; i < children.length; i++) {
                dotNetObject.invokeMethodAsync('SetIndex', i, children[i]!.id);
            }
        }
    });
}

export function CloseModal(modalId: string): void {
    if (typeof jQuery !== 'undefined') jQuery(modalId).modal('hide');
}

export function OpenModal(modalId: string): void {
    if (typeof jQuery !== 'undefined') jQuery(modalId).modal('show');
}

export function ShowMessage(message: string): void {
    console.log('Message:', message);
}

export function downloadFile(filename: string, content: string, contentType: string): void {
    const blob: Blob = new Blob([content], { type: contentType });
    const url: string = window.URL.createObjectURL(blob);
    const a: HTMLAnchorElement = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
}

export function SetFocusToElement(element: HTMLElement): void {
    element.focus();
}

export function OpenFullscreen(fullscreenId: string): void {
    const elem = document.getElementById(fullscreenId) as HTMLElement & {
        webkitRequestFullscreen?: () => void;
        msRequestFullscreen?: () => void;
    };
    if (!elem) return;
    if (elem.requestFullscreen) elem.requestFullscreen();
    else if (elem.webkitRequestFullscreen) elem.webkitRequestFullscreen();
    else if (elem.msRequestFullscreen) elem.msRequestFullscreen();
}

// Legacy theme functions (kept for backward compat with Blazor JSRuntime calls)

export function Cornerstone(): void {
    const s: CSSStyleDeclaration = document.documentElement.style;
    s.setProperty('--primary-color', 'var(--cornerstone-primary-color)');
    s.setProperty('--primary-border', 'var(--cornerstone-primary-border)');
    s.setProperty('--secondary-color', 'var(--cornerstone-secondary-color)');
    s.setProperty('--secondary-border', 'var(--cornerstone-secondary-border)');
    s.setProperty('--main-link-highlight', 'var(--cornerstone-link-highlight)');
    s.setProperty('--main-input-background', 'var(--cornerstone-input-background)');
    s.setProperty('--main-link-background-color', 'var(--cornerstone-link-background-color)');
    s.setProperty('--main-button-text', 'var(--cornerstone-button-text)');
    s.setProperty('--body-bg-color', 'var(--cornerstone-body-bg-color)');
    s.setProperty('--body-text-color', 'var(--cornerstone-body-text-color)');
    s.setProperty('--body-link-color', 'var(--cornerstone-body-link-color)');
    s.setProperty('--body-link-hover-color', 'var(--cornerstone-body-link-hover-color)');
    s.setProperty('--site-info-bg-selected', 'var(--cornerstone-site-info-bg-selected)');
    s.setProperty('--mainnav-bg-color', 'var(--cornerstone-mainnav-bg-color)');
    s.setProperty('--mainnav-text-color', 'var(--cornerstone-mainnav-text-color)');
    s.setProperty('--mainnav-link-color', 'var(--cornerstone-mainnav-link-color)');
    s.setProperty('--mainnav-link-hover-color', 'var(--cornerstone-mainnav-link-hover-color)');
    s.setProperty('--mainnav-link-active-color', 'var(--cornerstone-mainnav-link-active-color)');
    s.setProperty('--footer-bg-color', 'var(--cornerstone-footer-bg-color)');
    s.setProperty('--footer-text-color', 'var(--cornerstone-footer-text-color)');
    s.setProperty('--footer-link-color', 'var(--cornerstone-footer-link-color)');
    s.setProperty('--footer-link-hover-color', 'var(--cornerstone-footer-link-hover-color)');
    s.setProperty('--dashboard-bg', 'var(--cornerstone-dashboard-bg)');
}

export function InsLight(): void {
    const s: CSSStyleDeclaration = document.documentElement.style;
    s.setProperty('--primary-color', 'var(--ins-primary-color)');
    s.setProperty('--primary-border', 'var(--ins-primary-border)');
    s.setProperty('--secondary-color', 'var(--ins-secondary-color)');
    s.setProperty('--secondary-border', 'var(--ins-secondary-border)');
    s.setProperty('--main-link-highlight', 'var(--ins-link-highlight)');
    s.setProperty('--main-input-background', 'var(--ins-input-background)');
    s.setProperty('--main-link-background-color', 'var(--ins-link-background-color)');
    s.setProperty('--main-button-text', 'var(--ins-button-text)');
    s.setProperty('--body-bg-color', 'var(--ins-body-bg-color)');
    s.setProperty('--body-text-color', 'var(--ins-body-text-color)');
    s.setProperty('--body-link-color', 'var(--ins-body-link-color)');
    s.setProperty('--body-link-hover-color', 'var(--ins-body-link-hover-color)');
    s.setProperty('--site-info-bg-selected', 'var(--ins-site-info-bg-selected)');
    s.setProperty('--mainnav-bg-color', 'var(--ins-mainnav-bg-color)');
    s.setProperty('--mainnav-text-color', 'var(--ins-mainnav-text-color)');
    s.setProperty('--mainnav-link-color', 'var(--ins-mainnav-link-color)');
    s.setProperty('--mainnav-link-hover-color', 'var(--ins-mainnav-link-hover-color)');
    s.setProperty('--mainnav-link-active-color', 'var(--ins-mainnav-link-active-color)');
    s.setProperty('--footer-bg-color', 'var(--ins-footer-bg-color)');
    s.setProperty('--footer-text-color', 'var(--ins-footer-text-color)');
    s.setProperty('--footer-link-color', 'var(--ins-footer-link-color)');
    s.setProperty('--footer-link-hover-color', 'var(--ins-footer-link-hover-color)');
    s.setProperty('--dashboard-bg', 'var(--ins-dashboard-bg)');
}

export function InsDark(): void {
    const s: CSSStyleDeclaration = document.documentElement.style;
    s.setProperty('--primary-color', 'var(--ins-dark-primary-color)');
    s.setProperty('--primary-border', 'var(--ins-dark-primary-border)');
    s.setProperty('--secondary-color', 'var(--ins-dark-secondary-color)');
    s.setProperty('--secondary-border', 'var(--ins-dark-secondary-border)');
    s.setProperty('--main-link-highlight', 'var(--ins-dark-link-highlight)');
    s.setProperty('--main-input-background', 'var(--ins-dark-input-background)');
    s.setProperty('--main-link-background-color', 'var(--ins-dark-link-background-color)');
    s.setProperty('--main-button-text', 'var(--ins-dark-button-text)');
    s.setProperty('--body-bg-color', 'var(--ins-dark-body-bg-color)');
    s.setProperty('--body-text-color', 'var(--ins-dark-body-text-color)');
    s.setProperty('--body-link-color', 'var(--ins-dark-body-link-color)');
    s.setProperty('--body-link-hover-color', 'var(--ins-dark-body-link-hover-color)');
    s.setProperty('--site-info-bg-selected', 'var(--ins-dark-site-info-bg-selected)');
    s.setProperty('--mainnav-bg-color', 'var(--ins-dark-mainnav-bg-color)');
    s.setProperty('--mainnav-text-color', 'var(--ins-dark-mainnav-text-color)');
    s.setProperty('--mainnav-link-color', 'var(--ins-dark-mainnav-link-color)');
    s.setProperty('--mainnav-link-hover-color', 'var(--ins-dark-mainnav-link-hover-color)');
    s.setProperty('--mainnav-link-active-color', 'var(--ins-dark-mainnav-link-active-color)');
    s.setProperty('--footer-bg-color', 'var(--ins-dark-footer-bg-color)');
    s.setProperty('--footer-text-color', 'var(--ins-dark-footer-text-color)');
    s.setProperty('--footer-link-color', 'var(--ins-dark-footer-link-color)');
    s.setProperty('--footer-link-hover-color', 'var(--ins-dark-footer-link-hover-color)');
    s.setProperty('--dashboard-bg', 'var(--ins-dark-dashboard-bg)');
}
